import React, { useCallback, useEffect, useState } from 'react';
import { usePushEvents } from '../../hooks/PushEvents';
import {
    errorFacebookLogin,
    facebookLoginSuccess,
    facebookOnMainAuth,
} from '../../hooks/PushEvents/clevertapEvents';
import { webConfig } from '../../utils/webConfig';
import { useDonorLogin } from '../../providers/DonorProvider/useDonorLogin';
import { getFacebookProfilePicture } from '../../providers/DonorProvider/getFacebookProfilePicture';
import { useLoginToastContext } from '../../providers/LoginToastProvider';
import { setLoginTypeLocalStorage } from '../../providers/DonorProvider/donorLocalStorage';
import FacebookLoginButton from './FacebookLoginButton';
import { useDonorContext } from '../../providers/DonorProvider/DonorProvider';
import { FacebookAuthResponse } from '../../api';

export interface FacebookButtonProps {
    className?: string;
    onSuccess?: (facebookUser: FacebookAuthResponse) => unknown;
}

export const FacebookButton: React.FC<FacebookButtonProps> = ({
    className,
    onSuccess,
}) => {
    const { pushEvent } = usePushEvents();
    const { handleOpenLoginToast } = useLoginToastContext();
    const { facebookLogin, setProfilePicture } = useDonorLogin();
    const { donorContext } = useDonorContext();
    const [facebookUser, setFacebookUser] = useState<
        FacebookAuthResponse | undefined
    >();

    const loadProfilePicture = useCallback(
        async (facebookUser: FacebookAuthResponse) => {
            setProfilePicture(await getFacebookProfilePicture(facebookUser));
        },
        [setProfilePicture],
    );

    useEffect(() => {
        if (
            !donorContext.loginResponse?.data?.donorId ||
            donorContext.loginResponse?.error ||
            !facebookUser
        )
            return;
        pushEvent(facebookLoginSuccess);
        onSuccess?.(facebookUser);
        loadProfilePicture(facebookUser);
        handleOpenLoginToast();
        setLoginTypeLocalStorage(
            facebookUser.name,
            facebookUser.email,
            'facebook',
        );
    }, [
        donorContext.loginResponse?.data?.donorId,
        donorContext.loginResponse?.error,
        facebookUser,
        handleOpenLoginToast,
        loadProfilePicture,
        onSuccess,
        pushEvent,
    ]);

    const handleResponse = (userInfo: FacebookAuthResponse) => {
        if (userInfo.status === 'unknown') {
            pushEvent(errorFacebookLogin, { reason: JSON.stringify(userInfo) });
        } else {
            facebookLogin({
                facebookId: userInfo.userID,
                facebookToken: userInfo.accessToken,
                emailAddress: userInfo.email,
            });
            setFacebookUser(userInfo);
        }
    };

    const handleClick = () => {
        pushEvent(facebookOnMainAuth);
    };

    return (
        <FacebookLoginButton
            appId={webConfig.facebookAppId}
            onSuccess={handleResponse}
            onFailure={(response) => {
                if (response.status !== 'user_cancelled') {
                    pushEvent(errorFacebookLogin, {
                        reason: JSON.stringify(response),
                    });
                }
            }}
            className={className}
            onClick={handleClick}
            isDisabled={donorContext.loginResponse.loading}
        />
    );
};
